import { z } from 'zod';

/**
 * パスワードバリデーション（superRefine）
 *
 * パスワードは任意入力（空の場合はサーバー側で更新しない）。
 * パスワードを入力した場合のみ 8文字以上128文字以下・英数字混合・確認一致を検証する。
 *
 * partner-admin の EditUserForm にも同等の passwordRefinement が存在するが、意図的に共有化していない。
 * - app 間（apps/client ⇔ apps/partner-admin）に import 経路がなく、共有するには utils/common への
 *   抽出と両 app の移行が必要になる（本チケットのスコープ外）
 * - バリデーションルール自体も異なる（128文字上限・確認欄のみ入力時のエラーは本フォーム固有）
 * 共通化する場合は、client 側のルールを正として utils/common へ抽出する別チケットで対応する。
 */
const passwordRefinement = (
  data: { password?: string; passwordConfirmation?: string },
  ctx: z.RefinementCtx
): void => {
  const { password, passwordConfirmation } = data;

  // 確認欄のみ入力された場合
  if (!password && passwordConfirmation) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードを入力してください。',
      path: ['passwordConfirmation'],
    });
    return;
  }

  // パスワード未入力の場合は検証スキップ
  if (!password) return;

  // 8文字未満
  if (password.length < 8) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードは8文字以上入力してください。',
      path: ['password'],
    });
  }

  // 128文字超
  if (password.length > 128) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードは128文字以内で入力してください。',
      path: ['password'],
    });
  }

  // 英数字混合チェック
  if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードは英数字混合にしてください。',
      path: ['password'],
    });
  }

  // パスワード確認の一致チェック
  if (password !== passwordConfirmation) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードとパスワード（再入力）が一致しません。',
      path: ['passwordConfirmation'],
    });
  }
};

/**
 * 利用者編集フォームの Zod スキーマ。
 * billableFrom は必須。
 * password / passwordConfirmation は任意項目（入力した場合のみ検証）。
 *
 * 過去日付チェックは DatePickerFieldController（presentations 側）の
 * `fromDate={new Date()}` によるカレンダーUI上の選択不可制御のみで行う。
 * 編集フォームは既存レコード（過去日付のまま保存された billableFrom を持つ
 * ユーザーも実在する）を読み込むため、Zod 側にも過去日 refine を追加すると
 * 「値を変更していないのにフォームを開いただけでエラーになる」問題を避けるための
 * 追加ロジックが必要になり複雑化する。そのため本フォームでは UI 制御のみに統一する。
 */
export const editUserFormSchema = z
  .object({
    fullName: z.string().min(1, '氏名を入力してください'),
    email: z
      .string()
      .min(1, 'メールアドレスを入力してください')
      .email('有効なメールアドレスを入力してください'),
    branchId: z.string().optional(),
    billableFrom: z
      .date({ invalid_type_error: '利用開始日を選択してください。' })
      .nullable()
      .refine(v => v !== null, { message: '利用開始日を選択してください。' })
      .transform(v => v as Date),
    password: z.string().optional(),
    passwordConfirmation: z.string().optional(),
  })
  .superRefine(passwordRefinement);

export type EditUserFormValues = z.infer<typeof editUserFormSchema>;
